import { useCallback, useMemo } from 'react';
import { ThemeGroupContext } from '../contexts';
import type { CategoryDescriptor, Theme, ThemeGroupContextValue } from '../types';
import { SelectMenu } from './SelectMenu';

export type ThemeSelectorProps = {
  /** Category information (id and name) */
  category: CategoryDescriptor;
  /** Array of available themes for this category */
  themes: Theme[];
  /** ID of the currently selected theme */
  selectedThemeId: number;
  /** Callback when a theme is selected - receives categoryId and paletteId */
  onChange: (categoryId: number, paletteId: number) => void;
  /** Optional callback when delete button is clicked on a palette */
  onDeletePalette?: (paletteId: number) => void;
  /** Optional callback when edit button is clicked on a palette (for future modal integration) */
  onEditPalette?: (paletteId: number) => void;
  /** Optional callback when create theme button is clicked (for future modal integration) */
  onCreatePalette?: () => void;
  /** Whether user can create new themes */
  canCreate?: boolean;
  /** Whether user can edit existing themes */
  canEdit?: boolean;
  /** Whether user can delete existing themes */
  canDelete?: boolean;
  /** Whether the entire selector is disabled */
  disabled?: boolean;
};

export const ThemeSelector = ({
  category,
  themes,
  selectedThemeId,
  onChange,
  onDeletePalette,
  onEditPalette,
  onCreatePalette,
  canCreate = false,
  canEdit = false,
  canDelete = false,
  disabled = false,
}: ThemeSelectorProps): React.JSX.Element => {

  const filteredThemes = useMemo(() => {
    return themes.map(theme => ({
      ...theme,
      editable: theme.editable && (canEdit || canDelete),
    }));
  }, [themes, canEdit, canDelete]);

  const handleDeletePalette = useCallback((id: number) => {
    if (!disabled && canDelete && onDeletePalette) {
      onDeletePalette(id);
    }
  }, [disabled, canDelete, onDeletePalette]);

  const handleEditPalette = useCallback((id: number) => {
    if (!disabled && canEdit && onEditPalette) {
      onEditPalette(id);
    }
  }, [disabled, canEdit, onEditPalette]);

  const handleSetTheme = useCallback((id: number) => {
    if (!disabled) {
      onChange(category.id, id);
    }
  }, [disabled, category.id, onChange]);

  const handleCreatePalette = useCallback(() => {
    if (!disabled && canCreate && onCreatePalette) {
      onCreatePalette();
    }
  }, [disabled, canCreate, onCreatePalette]);

  const contextValue = useMemo<ThemeGroupContextValue>(() => ({
    deletePalette: handleDeletePalette,
    editPalette: handleEditPalette,
    editingId: null,
    selected: selectedThemeId,
    setTheme: handleSetTheme,
    themes: filteredThemes,
  }), [
    handleDeletePalette,
    handleEditPalette,
    selectedThemeId,
    handleSetTheme,
    filteredThemes,
  ]);

  return (
    <ThemeGroupContext.Provider value={contextValue}>
      <SelectMenu
        items={filteredThemes}
        showCreateButton={canCreate && !disabled}
        onCreateClick={handleCreatePalette}
        disabled={disabled}
      />
    </ThemeGroupContext.Provider>
  );
};

ThemeSelector.displayName = 'ChartTheming.ThemeSelector';
